import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import ComicCard from "@/components/base/ComicCard";
import { getComicById, getComicsByCategory } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));
  const [showAllChapters, setShowAllChapters] = useState(false);
  const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
              <i className="ri-emotion-sad-line text-3xl text-foreground-500"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-50 mb-2">
              漫画未找到
            </h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = getComicsByCategory(comic.category)
    .filter((c) => c.id !== comic.id)
    .slice(0, 6);

  const visibleChapters = showAllChapters
    ? comic.chapters
    : comic.chapters.slice(0, 20);

  const chapterCount = comic.chapters.length;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 韩漫在线观看`,
    description: comic.description,
    author: {
      "@type": "Person",
      name: comic.author,
    },
    datePublished: comic.chapters[chapterCount - 1]?.updateDate || comic.updateDate,
    dateModified: comic.updateDate,
    image: comic.coverImage,
    publisher: {
      "@type": "Organization",
      name: "韩漫漫画网站",
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "漫画分类", to: "/category/all" },
              {
                label:
                  categories.find((c) => c.slug === comic.category)?.name ||
                  comic.category,
                to: `/category/${comic.category}`,
              },
              { label: comic.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="flex-shrink-0 flex justify-center lg:justify-start">
              <div className="w-[240px] md:w-[280px]">
                <div className="w-full aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 免费韩国漫画下拉式阅读`}
                    title={`${comic.title} - 韩漫在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="text-sm text-foreground-400">
                  作者：{comic.author}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-600" />
                <span className="text-sm text-accent-500 font-medium">
                  <i className="ri-star-fill text-xs mr-1"></i>
                  {comic.rating}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-600" />
                <span className="text-sm text-foreground-500 whitespace-nowrap">
                  <i className="ri-eye-line text-xs mr-1"></i>
                  {comic.views >= 10000
                    ? `${(comic.views / 10000).toFixed(1)}万`
                    : comic.views}{" "}
                  次阅读
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-600" />
                <span
                  className={`text-sm font-medium whitespace-nowrap ${
                    comic.status === "连载中"
                      ? "text-secondary-400"
                      : "text-accent-400"
                  }`}
                >
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${
                      comic.status === "连载中"
                        ? "bg-secondary-500 animate-pulse"
                        : "bg-accent-500"
                    }`}
                  />
                  {comic.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-200/60 text-foreground-400 border border-background-300/40 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6 p-5 rounded-xl bg-background-100/70 border border-background-200/50">
                <h3 className="text-sm font-semibold text-foreground-200 mb-2">
                  作品简介
                </h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  {comic.longDescription}
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mt-6">
                {comic.chapters.length > 0 && (
                  <button
                    onClick={() => {
                      const firstChapter = comic.chapters[comic.chapters.length - 1];
                      setSelectedChapterId(firstChapter.id);
                    }}
                    className="px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-line mr-2"></i>
                    开始阅读第一话
                  </button>
                )}
                <button className="px-6 py-3 rounded-lg border border-background-300/50 text-foreground-300 hover:text-foreground-50 hover:border-foreground-50/50 text-sm transition-all duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-bookmark-line mr-2"></i>
                  加入收藏
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50">
                章节列表
                <span className="text-sm text-foreground-500 font-normal ml-2">
                  共 {chapterCount} 话
                </span>
              </h2>
              {chapterCount > 20 && (
                <button
                  onClick={() => setShowAllChapters(!showAllChapters)}
                  className="text-sm text-primary-500 hover:text-primary-400 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {showAllChapters ? "收起" : `查看全部 ${chapterCount} 话`}
                  {showAllChapters ? (
                    <i className="ri-arrow-up-s-line ml-1"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line ml-1"></i>
                  )}
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
              {visibleChapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => setSelectedChapterId(chapter.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    selectedChapterId === chapter.id
                      ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                      : "bg-background-100/70 text-foreground-400 hover:text-foreground-200 hover:bg-background-200/60 border border-background-200/40"
                  }`}
                >
                  <i className="ri-file-text-line text-xs"></i>
                  <span className="truncate">{chapter.title}</span>
                </button>
              ))}
            </div>
          </div>

          {selectedChapterId && (
            <div className="mt-8 p-8 rounded-xl bg-background-100/70 border border-background-200/50 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/20">
                <i className="ri-book-read-line text-2xl text-primary-500"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-100 mb-2">
                准备阅读 —— 第{selectedChapterId}话
              </h3>
              <p className="text-foreground-500 text-sm">
                本章节内容正在加载中，请稍候...韩漫在线观看平台为您提供流畅的免费韩国漫画下拉式阅读体验。
              </p>
            </div>
          )}

          {relatedComics.length > 0 && (
            <div className="mt-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-5 rounded-full bg-primary-500" />
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {relatedComics.map((c) => (
                  <div key={c.id} className="flex justify-center">
                    <ComicCard comic={c} size="small" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}